import { Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { KiteBrokerButtonBasket, TransactionType, ZerodhaOrderForm } from '../models/broker.model';
import { TradesDataModel } from '../models/trades.models';

/** What Zerodha reported for a basket on its way back to us. */
export type BasketOutcome = 'placed' | 'cancelled' | 'unknown';

/** The basket the user just returned from, as shown in the order status popup. */
export interface BasketResult {
  outcome: BasketOutcome;
  /** The legs we sent to Kite (empty if they couldn't be recovered). */
  legs: KiteBrokerButtonBasket[];
  /** When the user came back from Kite. */
  at: Date;
}

@Injectable({
  providedIn: 'root'
})
export class BrokerService {

    private readonly apiKey = environment.kiteAPIKey;

    /**
     * The basket the user just returned from, shown as a popup on the page the trade
     * was launched from (BrokerOrderStatusDialogComponent). Set by /broker/return,
     * cleared when the popup is closed.
     */
    readonly orderResult = signal<BasketResult | null>(null);

    /** sessionStorage key holding the page a trade was launched from. */
    static readonly RETURN_URL_KEY = 'zerodhaReturnUrl';
    /** sessionStorage key holding the basket sent to Kite, for the status popup. */
    static readonly BASKET_KEY = 'zerodhaLastBasket';
    /** A saved origin older than this (ms) is treated as stale and ignored. */
    private static readonly RETURN_URL_TTL = 15 * 60 * 1000;

    constructor() {}

    /**
     * Remember the in-app page a trade is launched from before we hand the
     * browser to Zerodha. After the user places or cancels the basket, Zerodha
     * redirects back to the Kite-registered URL (/broker/return) — we use this to
     * send the user back where they started.
     */
    static rememberTradeOrigin(): void {
      try {
        const url = window.location.pathname + window.location.search + window.location.hash;
        sessionStorage.setItem(
          BrokerService.RETURN_URL_KEY,
          JSON.stringify({ url, ts: Date.now() })
        );
      } catch { /* storage unavailable — ignore */ }
    }

    /** Save the basket being sent to Kite; read back by takeBasket() on return. */
    static rememberBasket(basket: KiteBrokerButtonBasket[]): void {
      try {
        sessionStorage.setItem(BrokerService.BASKET_KEY, JSON.stringify({ basket, ts: Date.now() }));
      } catch { /* storage unavailable — ignore */ }
    }

    /** Read and clear a recently-saved basket, or [] if none/stale. */
    static takeBasket(): KiteBrokerButtonBasket[] {
      try {
        const raw = sessionStorage.getItem(BrokerService.BASKET_KEY);
        if (!raw) { return []; }
        sessionStorage.removeItem(BrokerService.BASKET_KEY);
        const { basket, ts } = JSON.parse(raw) as { basket: KiteBrokerButtonBasket[]; ts: number };
        if (!Array.isArray(basket) || Date.now() - ts > BrokerService.RETURN_URL_TTL) { return []; }
        return basket;
      } catch {
        return [];
      }
    }

    /** Read and clear a recently-saved trade origin, or null if none/stale. */
    static takeTradeOrigin(): string | null {
      try {
        const raw = sessionStorage.getItem(BrokerService.RETURN_URL_KEY);
        if (!raw) { return null; }
        sessionStorage.removeItem(BrokerService.RETURN_URL_KEY);
        const { url, ts } = JSON.parse(raw) as { url: string; ts: number };
        if (!url || Date.now() - ts > BrokerService.RETURN_URL_TTL) { return null; }
        return url;
      } catch {
        return null;
      }
    }

    /**
     * Builds a Kite basket for an equity (NSE cash) trade placed via the order
     * dialog — used by every non-derivative segment.
     *
     * The product carries the segment intent: Intraday uses MIS, while the
     * delivery segments (Multiday / Positional / Investment) use CNC. Direction,
     * quantity, price and product all come from the user's edits in `form`.
     *
     * When a stop loss is set, a second protective leg is added: an SL-M
     * (stop-loss market) order in the opposite direction, triggered at the stop
     * loss price. Zerodha's Bracket Order (entry + SL in one order) was
     * discontinued in 2020, so a separate SL order is the correct way to attach
     * a stop loss today.
     */
    buildEquityOrderBasket(trade: TradesDataModel, form: ZerodhaOrderForm): KiteBrokerButtonBasket[] {
      const entry: KiteBrokerButtonBasket = {
        variety: 'regular',
        tradingsymbol: trade.symbol,           // plain equity symbol for NSE cash
        exchange: 'NSE',
        transaction_type: form.transaction_type,
        order_type: form.order_type,
        quantity: form.quantity,
        product: form.product,                 // 'MIS' (intraday) or 'CNC' (delivery)
        price: form.order_type === 'LIMIT' ? form.price : 0,
        trigger_price: form.trigger_price,
        readonly: false,
      };

      const basket: KiteBrokerButtonBasket[] = [entry];

      // Only intraday (MIS) gets the protective SL-M leg. Under CNC (delivery)
      // the opposite-side SELL would be a short delivery of shares not yet held,
      // which Zerodha drops from the basket — delivery stops need a GTT instead.
      if (form.product === 'MIS' && form.stop_loss != null && form.stop_loss > 0) {
        basket.push({
          variety: 'regular',
          tradingsymbol: trade.symbol,
          exchange: 'NSE',
          // Protective leg exits the position, so it's the opposite side.
          transaction_type: form.transaction_type === 'BUY' ? 'SELL' : 'BUY',
          order_type: 'SL-M',                  // triggers a market exit at the SL
          quantity: form.quantity,
          product: form.product,
          price: 0,
          trigger_price: form.stop_loss,
          readonly: false,
        });
      }

      return basket;
    }

    /**
     * Builds a pre-filled equity (NSE cash) basket for the one-click trade
     * buttons that don't open the order dialog.
     *
     * Product depends on the segment: intraday trades use MIS, while the
     * delivery-style strategies (Multiday / Positional / Investment) use CNC.
     * Quantity defaults to the instrument's lot size (1 if unavailable) — the
     * user reviews and adjusts the final size on the Zerodha basket page.
     */
    buildEquityBasket(trade: TradesDataModel, segment: string, quantity?: number): KiteBrokerButtonBasket[] {
      if (!trade) { return []; }
      const price = trade.entry || trade.close || 0;
      const qty = quantity && quantity > 0
        ? quantity
        : (trade.lot_size && trade.lot_size > 0 ? trade.lot_size : 1);
      const tradingsymbol = trade.symbol || trade.trading_symbol;
      const product = segment === 'Intraday' ? 'MIS' : 'CNC';
      const side: TransactionType = trade.alert === 1 ? 'BUY' : 'SELL';

      const basket: KiteBrokerButtonBasket[] = [{
        variety: 'regular',
        tradingsymbol,
        exchange: 'NSE',
        transaction_type: side,
        order_type: 'LIMIT',
        quantity: qty,
        product,
        price,
        trigger_price: null,
        readonly: false,
      }];

      // Attach the protective stop-loss leg (opposite side, SL-M) only for
      // intraday (MIS). The stop is a SELL for a long, and under CNC (delivery)
      // Zerodha rejects a sell of shares not yet held (a short delivery), so a
      // delivery protective leg is silently dropped from the basket — the reason
      // it never showed on the Positional/Multiday/Investment tabs. Delivery
      // stops need a GTT, which the basket endpoint can't express. Also skipped
      // when the stop is on the wrong side of the entry, so a market SL-M can't
      // fire the instant the basket is placed.
      const sl = trade.sl ?? 0;
      if (product === 'MIS' && sl > 0 && this.isProtectiveStopValid(side, price, sl)) {
        basket.push({
          variety: 'regular',
          tradingsymbol,
          exchange: 'NSE',
          transaction_type: side === 'BUY' ? 'SELL' : 'BUY',
          order_type: 'SL-M',
          quantity: qty,
          product,
          price: 0,
          trigger_price: sl,
          readonly: false,
        });
      }

      return basket;
    }

    /**
     * Builds a pre-filled F&O (NFO) basket for the one-click trade buttons
     * (Futures and Options rows).
     *
     * Derivatives trade the contract symbol (`trading_symbol`) on NFO with
     * product NRML, and quantity is the instrument's lot size. Price defaults to
     * the contract's last close.
     *
     * When the trade carries a stop loss, a second protective leg is added — an
     * SL-M (stop-loss market) order in the opposite direction, triggered at the
     * stop loss price — so the position is placed with its stop attached, the
     * same way the equity order dialog does it. The SL leg is skipped when the
     * stop sits on the wrong side of the entry (see isProtectiveStopValid),
     * because a market SL-M there would fire the instant the basket is placed.
     */
    buildFuturesBasket(trade: TradesDataModel, quantity?: number): KiteBrokerButtonBasket[] {
      if (!trade) { return []; }
      const qty = quantity && quantity > 0
        ? quantity
        : (trade.lot_size && trade.lot_size > 0 ? trade.lot_size : 1);
      const tradingsymbol = trade.trading_symbol || trade.symbol;
      const entryPrice = trade.close || 0;
      const side: TransactionType = trade.alert === 1 ? 'BUY' : 'SELL';

      const basket: KiteBrokerButtonBasket[] = [{
        variety: 'regular',
        tradingsymbol,
        exchange: 'NFO',
        transaction_type: side,
        order_type: 'LIMIT',
        quantity: qty,
        product: 'NRML',
        price: entryPrice,
        trigger_price: null,
        readonly: false,
      }];

      const sl = trade.sl ?? 0;
      if (sl > 0 && this.isProtectiveStopValid(side, entryPrice, sl)) {
        basket.push({
          variety: 'regular',
          tradingsymbol,
          exchange: 'NFO',
          // Protective leg exits the position, so it's the opposite side.
          transaction_type: side === 'BUY' ? 'SELL' : 'BUY',
          order_type: 'SL-M',          // triggers a market exit at the SL
          quantity: qty,
          product: 'NRML',
          price: 0,
          trigger_price: sl,
          readonly: false,
        });
      }

      return basket;
    }

    /**
     * True when a stop-loss price sits on the losing side of the entry, so a
     * protective SL-M leg won't trigger the moment it's placed. For a BUY the
     * stop must be below the entry; for a SELL, above. When the entry price is
     * unknown (0) the check is skipped — we can't validate it, so we trust the
     * source data and let the leg through.
     */
    private isProtectiveStopValid(side: TransactionType, entryPrice: number, sl: number): boolean {
      if (!(entryPrice > 0)) { return true; }
      return side === 'BUY' ? sl < entryPrice : sl > entryPrice;
    }

    zerodhaTradeExecute(basketData: KiteBrokerButtonBasket[]): void {
      if (!basketData?.length) {
        console.warn('KiteService: No basket data provided.');
        return;
      }
  
      // Remember where we are so we can return here after Zerodha redirects back,
      // and what was sent, so the status popup can show the order.
      BrokerService.rememberTradeOrigin();
      BrokerService.rememberBasket(basketData);

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://kite.zerodha.com/connect/basket';

      const apiKeyInput = document.createElement('input');
      apiKeyInput.type = 'hidden';
      apiKeyInput.name = 'api_key';
      apiKeyInput.value = this.apiKey;
      form.appendChild(apiKeyInput);
  
      const dataInput = document.createElement('input');
      dataInput.type = 'hidden';
      dataInput.name = 'data';
      dataInput.value = JSON.stringify(basketData);
      form.appendChild(dataInput);
  
      document.body.appendChild(form);
      form.submit();
      form.remove();
    }


}
